import flagsmith, { IFlags } from 'flagsmith';
import { toast } from 'sonner';
import { FLAGSMITH_DEFAULT_FLAGS, FLAGSMITH_ENV_ID } from '../config/flagsmith';

// Initialize state with default values
let flagsInitialized = false;
let currentFlags: IFlags<string> = {};
let currentUserId: string | null = null;
let lastIdentifiedUserId: string | null = null;
let lastTraitUpdates: Record<string, { value: any; timestamp: number }> = {};
// Store all traits that have been set for the current user session
let userTraits: Record<string, any> = {};
let identifyDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let traitDebounceTimer: ReturnType<typeof setTimeout> | null = null;

// Debounce time in milliseconds
const DEBOUNCE_TIME = 500;

/**
 * Initialize Flagsmith with the environment ID from config
 * Returns a promise that resolves when initialization is complete
 */
export const initFlagsmith = (): Promise<void> => {
  if (flagsInitialized) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    flagsmith.init({
      environmentID: FLAGSMITH_ENV_ID,
      onChange: (_previousFlags: IFlags<string> | null) => {
        // Update our current flags reference
        currentFlags = { ...flagsmith.getAllFlags() };

        // Log flag changes in development

        // Handle specific flag changes here if needed
        // Example:
        // if (hasFeatureChanged('my_feature', previousFlags)) {
        //   // Do something when this specific feature changes
        // }
      },
      onError: (error) => {
        console.error('Flagsmith error:', error);
        if (import.meta.env.DEV) {
          toast.error('Failed to connect to Flagsmith');
        }
      },
      cacheFlags: true,
      enableLogs: import.meta.env.DEV,
      // Set default flags before the real ones load
      defaultFlags: FLAGSMITH_DEFAULT_FLAGS,
    });

    flagsInitialized = true;
    resolve();
  });
};

/**
 * Check if a feature is enabled
 */
export const hasFeature = (featureId: string): boolean => {
  return flagsmith.hasFeature(featureId);
};

/**
 * Get the value of a feature
 */
export const getFeatureValue = <T>(featureId: string, defaultValue?: T): T => {
  try {
    return flagsmith.getValue(featureId) as T;
  } catch (error) {
    if (defaultValue !== undefined) {
      return defaultValue;
    }
    throw error;
  }
};

/**
 * Check if a feature's value has changed
 */
export const hasFeatureChanged = (
  featureId: string,
  previousFlags: IFlags<string> | null
): boolean => {
  const newValue = flagsmith.getValue(featureId);
  const oldValue = previousFlags?.[featureId]?.value;

  return newValue !== oldValue;
};

/**
 * Get all feature flags
 */
export const getAllFeatures = (): IFlags<string> => {
  return { ...currentFlags };
};

/**
 * Identify a user to Flagsmith with debouncing to prevent duplicate calls
 * @param userId The unique identifier for the user
 */
export const identifyUser = (userId: string): void => {
  if (!flagsInitialized) {
    console.warn('Flagsmith not initialized when trying to identify user');
    return;
  }

  // Skip if this is the same user we already identified
  if (lastIdentifiedUserId === userId) {
    return;
  }

  // Clear any pending identify calls
  if (identifyDebounceTimer) {
    clearTimeout(identifyDebounceTimer);
  }

  // Set a debounce timer to prevent rapid consecutive calls
  identifyDebounceTimer = setTimeout(() => {
    // If this is a different user than before, reset the traits cache
    if (currentUserId !== userId) {
      userTraits = {};
    }

    currentUserId = userId;
    lastIdentifiedUserId = userId;
    flagsmith.identify(userId);

    // Apply any queued trait updates after identification completes
    const queuedTraits = Object.entries(lastTraitUpdates);
    if (queuedTraits.length > 0) {
      // Wait a small amount of time to ensure identification has completed
      setTimeout(() => {
        queuedTraits.forEach(([key, { value }]) => {
          flagsmith.setTrait(key, value);

          // Also store in the session-long cache
          userTraits[key] = value;
        });
      }, 100);
    }

    identifyDebounceTimer = null;
  }, DEBOUNCE_TIME);
};

/**
 * Set a trait for the identified user with debouncing to prevent duplicate calls
 * @param traitKey The trait key (e.g., 'email')
 * @param traitValue The trait value
 */
export const setUserTrait = (
  traitKey: string,
  traitValue: string | number | boolean
): void => {
  if (!flagsInitialized) {
    console.warn('Flagsmith not initialized when trying to set user trait');
    return;
  }

  if (!currentUserId) {
    console.warn(
      'No user identified when trying to set trait. Call identifyUser first.'
    );

    // Store the trait to be set once a user is identified
    if (identifyDebounceTimer) {
      // If there's a pending identify operation, queue this trait to be set after it completes
      const traitKey_str = String(traitKey);
      lastTraitUpdates[traitKey_str] = {
        value: traitValue,
        timestamp: Date.now(),
      };
    }

    return;
  }

  // Skip if this is the same trait value we already set recently
  const traitKey_str = String(traitKey);
  const lastUpdate = lastTraitUpdates[traitKey_str];
  const now = Date.now();

  // Check if the trait already exists with the same value in our session cache
  if (userTraits[traitKey_str] === traitValue) {
    return;
  }

  // Also check the short-term cache for very recent updates (5 seconds)
  if (
    lastUpdate &&
    lastUpdate.value === traitValue &&
    now - lastUpdate.timestamp < 5000
  ) {
    // Skip if same value set within last 5 seconds
    return;
  }

  // Clear any pending trait updates for this key
  if (traitDebounceTimer) {
    clearTimeout(traitDebounceTimer);
  }

  // Set a debounce timer to prevent rapid consecutive calls
  traitDebounceTimer = setTimeout(() => {
    flagsmith.setTrait(traitKey, traitValue);

    // Record this update in both caches
    lastTraitUpdates[traitKey_str] = {
      value: traitValue,
      timestamp: Date.now(),
    };

    // Store in the session-long cache
    userTraits[traitKey_str] = traitValue;

    traitDebounceTimer = null;
  }, DEBOUNCE_TIME);
};

/**
 * Clear the current user identification
 */
export const clearUserIdentity = (): void => {
  if (currentUserId) {
    // Clear any pending timers
    if (identifyDebounceTimer) {
      clearTimeout(identifyDebounceTimer);
      identifyDebounceTimer = null;
    }

    if (traitDebounceTimer) {
      clearTimeout(traitDebounceTimer);
      traitDebounceTimer = null;
    }

    // Reset all tracking variables
    currentUserId = null;
    lastIdentifiedUserId = null;
    lastTraitUpdates = {};
    userTraits = {}; // Clear the stored traits

    // The Flagsmith SDK doesn't have a direct method to clear identity,
    // but we can reset our tracking of the current user
  }
};

export default {
  init: initFlagsmith,
  hasFeature,
  getValue: getFeatureValue,
  getAllFeatures,
  identifyUser,
  setUserTrait,
  clearUserIdentity,
};
